import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const THEME_STORAGE_KEY = 'gematria_theme_mode';

export const darkColors = Object.freeze({
  primary: '#67B7F7',
  secondary: '#A8B3BE',
  background: '#0F141A',
  surface: '#182028',
  surfaceElevated: '#202A34',
  inputBackground: '#111820',
  mutedSurface: '#26313C',
  text: '#F2F5F7',
  textSecondary: '#B3BEC8',
  lightText: '#B3BEC8',
  border: '#3C4854',
  divider: '#2B3641',
  lightBorder: '#2B3641',
  onPrimary: '#06131D',
  onSecondary: '#10151A',
  onError: '#FFFFFF',
  overlay: 'rgba(0, 0, 0, 0.64)',
  shadow: '#000000',
  successSurface: '#143824',
  primarySurface: '#142F45',
  dangerSurface: '#402025',
  warning: '#FFB74D',
  warningSurface: '#3D2E16',
  white: '#182028',
  highlight: '#142F45',
  error: '#FF6B6B',
  success: '#4CD17A',
});

export const lightColors = Object.freeze({
  primary: '#1565C0',
  secondary: '#455A64',
  background: '#F4F6F8',
  surface: '#FFFFFF',
  surfaceElevated: '#FFFFFF',
  inputBackground: '#FFFFFF',
  mutedSurface: '#E9EEF3',
  text: '#1C252E',
  textSecondary: '#52606D',
  lightText: '#52606D',
  border: '#C7D0D9',
  divider: '#E1E6EB',
  lightBorder: '#E1E6EB',
  onPrimary: '#FFFFFF',
  onSecondary: '#FFFFFF',
  onError: '#FFFFFF',
  overlay: 'rgba(17, 24, 39, 0.48)',
  shadow: '#000000',
  successSurface: '#E7F6EC',
  primarySurface: '#E8F2FC',
  dangerSurface: '#FDECEC',
  warning: '#9A5A00',
  warningSurface: '#FFF4E5',
  white: '#FFFFFF',
  highlight: '#E8F2FC',
  error: '#C62828',
  success: '#147D3F',
});

let _isDark = false;
const _listeners = new Set();

const notifyListeners = () => {
  _listeners.forEach(listener => {
    try {
      listener(_isDark);
    } catch (error) {
      console.error('Theme listener failed:', error);
    }
  });
};

export const getIsDark = () => _isDark;
export const getThemeColors = () => (_isDark ? darkColors : lightColors);

export const toggleTheme = async () => {
  _isDark = !_isDark;
  notifyListeners();

  try {
    const value = _isDark ? 'dark' : 'light';
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      localStorage.setItem(THEME_STORAGE_KEY, value);
    } else {
      await AsyncStorage.setItem(THEME_STORAGE_KEY, value);
    }
  } catch (error) {
    console.warn('Unable to persist theme preference:', error);
  }
};

export const loadTheme = async () => {
  try {
    const stored = Platform.OS === 'web' && typeof localStorage !== 'undefined'
      ? localStorage.getItem(THEME_STORAGE_KEY)
      : await AsyncStorage.getItem(THEME_STORAGE_KEY);
    const nextIsDark = stored === 'dark';

    if (nextIsDark !== _isDark) {
      _isDark = nextIsDark;
      notifyListeners();
    }
  } catch (error) {
    console.warn('Unable to load theme preference:', error);
  }

  return _isDark;
};

export const subscribe = (listener) => {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
};
